/**
 * @license
 * @copyright Copyright Motili Inc., 2025 All Rights Reserved
 */

/* eslint-disable react/jsx-props-no-spreading */

import {
    Autocomplete,
    TextField,
    Checkbox as MuiCheckbox,
    Box,
    Typography,
    Button,
    styled,
    Chip,
    PaperProps,
    Paper,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import Check from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Checkbox } from '@motili/ui';
import { DisplayType } from 'common/types';
import { useMemo } from 'react';

const StyledContainer = styled(Box)({
    fontFamily: '"Nunito Sans", sans-serif',
    width: '100%',
    '& .MuiAutocomplete-root.MuiAutocomplete-hasClearIcon.MuiAutocomplete-hasPopupIcon .MuiOutlinedInput-root':
        {
            paddingRight: '80px !important',
        },
    '& .MuiAutocomplete-root .MuiOutlinedInput-root': {
        borderRadius: 0,
        padding: '4px 8px',
        minHeight: '40px',
        '& .MuiOutlinedInput-input': {
            padding: '5px',
            fontFamily: '"Nunito Sans", sans-serif',
            fontSize: '14px',
            fontWeight: 400,
        },
        '& fieldset': {
            borderColor: '#ccc',
        },
        '&:hover fieldset': {
            borderColor: '#ccc',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#05A6E6',
            borderWidth: '1px',
            boxShadow: '0px 0px 8px 0px #66AFE999',
        },
        '& .MuiAutocomplete-clearIndicator': {
            borderRadius: 0,
            visibility: 'visible',
            width: 'auto',
            padding: '0 4px',
        },
    },
});

const icon = <CheckBoxOutlineBlankIcon sx={{ display: 'none' }} />;
const checkedIcon = <Check />;

export function MultiCheckboxAutocomplete<T extends DisplayType>({
    label,
    options,
    value,
    onChange,
    placeholder,
    disabled = false,
    useMuiCheckbox = false,
}: {
    label: string;
    options: T[];
    value: T[];
    onChange: (value: T[]) => void;
    placeholder?: string;
    disabled?: boolean;
    useMuiCheckbox?: boolean;
}) {
    const placeHolderText = useMemo(
        () => (value && value.length ? undefined : placeholder),
        [value, placeholder]
    );
    const disabledSelectAll = useMemo(
        () => value?.length === options?.length,
        [value, options]
    );
    const disabledDeselectAll = useMemo(() => value.length === 0, [value]);

    const handleSelectAll = () => {
        onChange(options);
    };
    const handleDeselectAll = () => {
        onChange([]);
    };

    return (
        <StyledContainer id={`container-${label}`}>
            <Typography
                sx={{
                    fontFamily: 'inherit',
                    fontWeight: 700,
                    fontSize: 12,
                    mb: '5px',
                    display: 'block',
                    color: '#333',
                }}
            >
                {label}
            </Typography>

            <Autocomplete
                disabled={disabled}
                multiple
                disablePortal={false}
                options={options}
                value={value}
                onChange={(_, newValue) => onChange(newValue)}
                disableCloseOnSelect
                getOptionLabel={option => option.display}
                isOptionEqualToValue={(option, value) =>
                    option.id === value?.id
                }
                noOptionsText=''
                clearIcon={
                    <Typography
                        variant='caption'
                        sx={{
                            color: '#57C0E8',
                            fontSize: '11.2px',
                            fontWeight: 600,
                            fontFamily: 'Nunito Sans',
                            textTransform: 'uppercase',
                            cursor: 'pointer',
                            // '&:hover': { textDecoration: 'underline' },
                        }}
                    >
                        CLEAR
                    </Typography>
                }
                renderValue={(value, renderValue) =>
                    value.map((option, index) => (
                        <Chip
                            label={option.display}
                            size='small'
                            deleteIcon={<CloseIcon />}
                            sx={{
                                fontFamily: 'inherit',
                                fontSize: '12px',
                                borderRadius: '2px',
                                backgroundColor: '#EDF8FC',
                                border: '0px solid #ccc',
                                flexDirection: 'row-reverse',
                                '& .MuiChip-label': {
                                    color: '#333',
                                    fontFamily: 'Nunito Sans',
                                    fontWeight: 400,
                                    fontSize: '12px',
                                },
                                '& .MuiChip-deleteIcon': {
                                    color: '#999',
                                    ml: '4px',
                                    mr: '-4px',
                                },
                            }}
                            {...renderValue({ index })}
                        />
                    ))
                }
                renderOption={(props, option, { selected }) => (
                    <li {...props}>
                        {useMuiCheckbox ? (
                            <>
                                <MuiCheckbox
                                    checked={selected}
                                    icon={icon}
                                    checkedIcon={checkedIcon}
                                    size='medium'
                                    sx={{
                                        p: 0,
                                        color: '#ccc',
                                        width: '20px',
                                        height: '20px',
                                        borderRadius: '3px',
                                        border: '1px solid #ccc',
                                        '&.Mui-checked': {
                                            color: '#57C1E9',
                                        },
                                        '& .MuiSvgIcon-root': {
                                            width: '15px',
                                            height: '15px',
                                        },
                                    }}
                                />
                                <Typography
                                    sx={{
                                        fontFamily: 'Nunito Sans',
                                        fontSize: '14px',
                                        fontWeight: 400,
                                        color: '#333',
                                    }}
                                >
                                    {option.display}
                                </Typography>
                            </>
                        ) : (
                            <Checkbox
                                id={`${label}-${option.id || option.display}`}
                                name={`${label}-${option.id || option.display}`}
                                label={option.display}
                                value={option.id}
                                checked={selected}
                                onChange={() => {}}
                            />
                        )}
                    </li>
                )}
                renderInput={params => (
                    <TextField {...params} placeholder={placeHolderText} />
                )}
                slots={{
                    paper: CustomDropdownPaper,
                }}
                slotProps={{
                    paper: {
                        onSelectAll: handleSelectAll,
                        onDeselectAll: handleDeselectAll,
                        disabledSelectAll,
                        disabledDeselectAll,
                        sx: {
                            borderRadius: 0,
                            border: '1px solid #ccc',
                            boxShadow: '0px 2px 6px 0px #00000040',
                            '& .MuiAutocomplete-listbox': {
                                maxHeight: '300px',
                                fontFamily: 'Nunito Sans',
                                '& .MuiAutocomplete-option': {
                                    p: '6px 12px',
                                    gap: '10px',
                                    '&[aria-selected="true"]': {
                                        backgroundColor: '#edf8fc',
                                    },
                                },
                            },
                        },
                    } as any,
                }}
            />
        </StyledContainer>
    );
}

const CustomDropdownPaper = (
    props: PaperProps & {
        onSelectAll?: () => void;
        onDeselectAll?: () => void;
        disabledSelectAll?: boolean;
        disabledDeselectAll?: boolean;
    }
) => {
    const {
        children,
        onSelectAll,
        onDeselectAll,
        disabledSelectAll,
        disabledDeselectAll,
        ...other
    } = props;

    return (
        <Paper
            {...other}
            sx={{
                borderRadius: 0,
                border: '1px solid #ccc',
                mt: 0.5,
                boxShadow: '0px 4px 12px rgba(0,0,0,0.1)',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '12px 7px 0 7px',
                    alignItems: 'center',
                    '& .MuiButton-root': {
                        p: '0 6px',
                        textTransform: 'none',
                        fontSize: '14px',
                        fontWeight: 600,
                        fontFamily: 'Nunito Sans',
                    },
                }}
            >
                <Button
                    disabled={disabledSelectAll}
                    onMouseDown={e => {
                        e.preventDefault();
                        e.stopPropagation();
                    }}
                    onClick={onSelectAll}
                    sx={{
                        color: '#57C0E8',
                    }}
                >
                    Select All
                </Button>
                <Button
                    disabled={disabledDeselectAll}
                    onMouseDown={e => {
                        e.preventDefault();
                        e.stopPropagation();
                    }}
                    onClick={onDeselectAll}
                    sx={{
                        color: '#E63232',
                    }}
                >
                    Deselect All
                </Button>
            </Box>
            {children}
        </Paper>
    );
};
